// src/components/deals.jsx
// DealModal (create/edit/delete) and DealsPipeline kanban view.

import { useState, useEffect } from "react";
import * as HS from "../hubspot.js";
import { STAGES, ABM_STAGES, PIPELINES, STAGE_COLORS } from "../constants.js";
import { DEFAULT_ALLOWED_PIPELINES } from "../companyBuckets.js";
import { Btn, FI, LinkBtn, Modal, Spin } from "./ui.jsx";

// Safely format a close date for display. Accepts YYYY-MM-DD, ISO strings, or
// epoch ms. Returns "" for anything unparseable so we never render
// "Invalid Date" in the UI.
function fmtCloseDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // Pure YYYY-MM-DD: parse with noon local time to dodge timezone day-shifts
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) {
    const d = new Date(parseInt(ymd[1]), parseInt(ymd[2]) - 1, parseInt(ymd[3]), 12, 0, 0);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
  }
  // Epoch ms (numeric string)
  const ms = /^\d+$/.test(s) ? parseInt(s) : Date.parse(s);
  if (isNaN(ms)) return "";
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
}

export function DealModal({deal, companies, onSave, onDelete, onClose, defaultPipeline = "mattress"}) {
  const isNew = !deal;
  const [name,      setName]      = useState(deal?.name || "");
  // Track which pipeline the deal belongs to. Existing deals carry it from
  // hubspot.js normalization; new deals default to the parent kanban's tab.
  const [pipeline,  setPipeline]  = useState(deal?.pipeline || defaultPipeline);
  // Available stages depend on which pipeline the deal lives in
  const pipelineDef = PIPELINES.find(p => p.id === pipeline) || PIPELINES[0];
  const stagesForPipeline = pipelineDef.stages;
  const [stage,     setStage]     = useState(() => {
    if (deal?.stage) return deal.stage;
    return pipeline === "abm" ? "Quote Sent" : "Quote Requested";
  });
  const [amount,    setAmount]    = useState(deal?.amount != null ? String(deal.amount) : "");
  const [coId,      setCoId]      = useState(deal?.coId || deal?.companyId || companies[0]?.id || "");
  const [closeDate, setCloseDate] = useState(() => {
    // Coerce whatever's stored into YYYY-MM-DD for the <input type="date">
    const raw = deal?.closeDate;
    if (!raw) return "";
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const ms = /^\d+$/.test(s) ? parseInt(s) : Date.parse(s);
    if (isNaN(ms)) return "";
    const d = new Date(ms);
    if (isNaN(d.getTime())) return "";
    return d.getUTCFullYear() + "-" +
           String(d.getUTCMonth() + 1).padStart(2, "0") + "-" +
           String(d.getUTCDate()).padStart(2, "0");
  });
  const [notes,     setNotes]     = useState(deal?.notes || "");
  const [loading,   setLoading]   = useState(false);
  const [delConf,   setDelConf]   = useState(false);
  const [err,       setErr]       = useState("");

  const save = async () => {
    setErr("");
    if (!name.trim()) { setErr("Deal name is required."); return; }
    if (!coId)        { setErr("Please select a company."); return; }
    setLoading(true);
    const payload = {
      id:        deal?.id || "d_" + Date.now(),
      name:      name.trim(),
      stage,
      pipeline,
      amount:    parseFloat(amount) || 0,
      coId,
      companyId: coId,
      closeDate,
      notes,
    };
    try {
      if (isNew) {
        const res = await HS.createDeal(payload, coId);
        if (res.id) payload.id = res.id;
      } else {
        await HS.updateDeal(payload.id, payload);
      }
    } catch (e) {
      console.error("Deal save to HubSpot failed:", e.message);
    }
    setLoading(false);
    onSave(payload);
  };

  const deleteDeal = async () => {
    setLoading(true);
    try { await HS.deleteDeal(deal.id); }
    catch (e) { console.error("Deal delete failed:", e.message); }
    setLoading(false);
    onDelete(deal.id);
  };

  return (
    <Modal title={isNew ? "➕ New Deal" : "✏️ Edit Deal"} onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <FI label="Deal Name" value={name} onChange={setName} placeholder="e.g. Spring Mattress Order" />
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Pipeline</label>
            <select value={pipeline} onChange={e => {
              const newP = e.target.value;
              setPipeline(newP);
              // Reset stage to a valid stage in the new pipeline if current
              // stage doesn't exist there
              const newStages = (PIPELINES.find(p => p.id === newP) || PIPELINES[0]).stages;
              if (!newStages.includes(stage)) {
                setStage(newStages[0]);
              }
            }}
              style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,outline:"none",cursor:"pointer"}}>
              {PIPELINES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Stage</label>
            <select value={stage} onChange={e=>setStage(e.target.value)}
              style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,outline:"none"}}>
              {stagesForPipeline.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Amount ($)</label>
            <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0"
              style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor="var(--warm3)"}
              onBlur={e=>e.target.style.borderColor="var(--warm2)"}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Company</label>
            <select value={coId} onChange={e=>setCoId(e.target.value)}
              style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,outline:"none"}}>
              <option value="">- Select company -</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.properties.name}</option>)}
            </select>
          </div>
          <FI label="Expected Close Date" value={closeDate} onChange={setCloseDate} type="date" />
        </div>
        <FI label="Notes" value={notes} onChange={setNotes} multiline rows={3} placeholder="Any details about this deal..." />

        <div style={{display:"flex",gap:0,borderRadius:10,overflow:"hidden",border:"1px solid var(--warm2)"}}>
          {stagesForPipeline.map((s, i) => (
            <div key={s} onClick={()=>setStage(s)}
              style={{
                flex:1,padding:"8px 4px",textAlign:"center",fontSize:10,
                fontWeight:stage === s ? 700 : 400,cursor:"pointer",transition:"all .15s",
                background:stage === s ? STAGE_COLORS[s] : "#fff",
                color:stage === s ? "#fff" : "var(--muted)",
                borderRight:i < stagesForPipeline.length-1 ? "1px solid var(--warm2)" : "none",
              }}>
              {s.split(" ")[0]}
            </div>
          ))}
        </div>

        {err && <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13}}>{err}</div>}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
          <div>
            {!isNew && !delConf && (
              <button onClick={()=>setDelConf(true)} style={{padding:"8px 16px",borderRadius:99,border:"1px solid #f5c6c6",background:"#fdecea",fontSize:12,color:"var(--red)",cursor:"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>🗑 Delete Deal</button>
            )}
            {delConf && (
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:12,color:"var(--red)"}}>Delete from HubSpot?</span>
                <button onClick={deleteDeal} disabled={loading} style={{padding:"6px 14px",borderRadius:99,border:"none",background:"var(--red)",color:"#fff",fontSize:12,cursor:"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>
                  {loading ? <Spin/> : "Confirm"}
                </button>
                <button onClick={()=>setDelConf(false)} style={{padding:"6px 14px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:12,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>Cancel</button>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={loading}>
              {loading ? <><Spin/> {isNew ? "Creating..." : "Saving..."}</> : isNew ? "Create Deal in HubSpot" : "Save to HubSpot"}
            </Btn>
          </div>
        </div>

        <div style={{padding:"10px 14px",borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)",fontSize:11,color:"var(--muted)",display:"flex",alignItems:"center",gap:8}}>
          <span>🔗</span>
          <span>{isNew ? "This deal will be created in HubSpot and linked to the selected company." : "Changes will be written back to HubSpot automatically."}</span>
        </div>
      </div>
    </Modal>
  );
}

export function DealsPipeline({companies, currentUser, onOpenCompany, onUpdateCompanies}) {
  const [editDeal,   setEditDeal]   = useState(null);
  const [showNew,    setShowNew]    = useState(false);
  const [savingMsg,  setSavingMsg]  = useState("");

  // Pipeline tab toggle. Persists across reloads.
  const [activePipeline, setActivePipeline] = useState(() => {
    try { return localStorage.getItem("crm_active_pipeline") || "mattress"; } catch { return "mattress"; }
  });
  const setActivePipelinePersist = (id) => {
    setActivePipeline(id);
    try { localStorage.setItem("crm_active_pipeline", id); } catch {}
  };

  // ── Per-user pipeline visibility ──────────────────────────────────────────
  // Admin sees all pipelines configured in PIPELINES. Non-admin reps see only
  // the pipelines in their `allowedPipelines` list, defaulting to Mattress +
  // ABM. Note: PIPELINES in constants.js may not include all 7 SALES_PIPELINES
  // ids -- only ones actually displayed in the Pipeline tab. The set of tabs
  // shown is the intersection of (a) pipelines defined in PIPELINES and (b)
  // the rep's allowedPipelines. Admin sees all defined.
  const isAdmin = !!currentUser?.isAdmin;
  const userAllowedPipelines = Array.isArray(currentUser?.allowedPipelines)
    ? currentUser.allowedPipelines
    : DEFAULT_ALLOWED_PIPELINES;
  const visiblePipelines = isAdmin
    ? PIPELINES
    : PIPELINES.filter(p => userAllowedPipelines.includes(p.id));

  // If the persisted active pipeline isn't one the rep is allowed to see,
  // bump them to the first visible one. Side effect on mount + when their
  // permission set changes (e.g. admin toggles a chip while they're logged in).
  // Deps intentionally exclude `activePipeline` -- we only want to re-check
  // when the rep's permissions change, not when they switch tabs.
  useEffect(() => {
    if (visiblePipelines.length === 0) return;  // no pipelines allowed; nothing to fix
    if (!visiblePipelines.some(p => p.id === activePipeline)) {
      setActivePipelinePersist(visiblePipelines[0].id);
    }
  }, [userAllowedPipelines.join(",")]);  // re-run when permission set changes

  // Empty-state guard: rep has no allowed pipelines at all. Render an empty
  // tab with a hint message rather than crashing on `.find(...)` returning
  // undefined below.
  if (visiblePipelines.length === 0) {
    return (
      <div style={{animation:"fadeUp .35s ease both",padding:60,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:12,opacity:.4}}>🔒</div>
        <div style={{fontSize:15,fontWeight:600,color:"var(--brown)",marginBottom:6}}>No pipeline access</div>
        <div style={{fontSize:13,color:"var(--muted)"}}>Contact your admin to enable pipeline access for your account.</div>
      </div>
    );
  }

  // Find the current pipeline definition + stages -- use visiblePipelines, not
  // PIPELINES, so disallowed pipelines can't sneak in via a stale localStorage
  // value or admin revoking access mid-session.
  const currentPipeline = visiblePipelines.find(p => p.id === activePipeline) || visiblePipelines[0];
  const currentStages = currentPipeline.stages;

  // Flatten all deals across all companies, tagged with parent company info.
  // Each deal already has a `pipeline` field set by hubspot.js (defaults to
  // "mattress" for legacy / unmapped stages).
  //
  // OWNERSHIP FILTER: if the rep has seeAll=false, also restrict to deals
  // owned by them. We check the parent company's ownerId since deals on a
  // company the rep already can't see have been pre-filtered out by
  // App.jsx's visibleCompanies (and any owner-mismatched deals on companies
  // they CAN see -- e.g. seeAll=true reps -- would still be hidden here).
  // Admin sees everything regardless.
  const allDeals = companies.flatMap(c =>
    (c.deals || []).map(d => ({...d, coName: c.properties.name, coId: c.id, coOwnerId: c.ownerId}))
  );

  const ownedDealsOnly = !isAdmin && currentUser && currentUser.seeAll === false;
  const ownerIdsToMatch = ownedDealsOnly ? [
    currentUser.id,
    currentUser.hubspotOwnerId,
    (currentUser.id || "").startsWith("hs_") ? currentUser.id.slice(3) : null,
  ].filter(Boolean) : null;

  // Visible-pipeline filter: drop deals whose pipeline isn't one the rep can
  // see (defense-in-depth -- the active-pipeline filter below also gates).
  // Then optional ownership filter on top.
  const reachableDeals = allDeals.filter(d => {
    const p = d.pipeline || HS.pipelineForStage(d.stageId || "");
    if (!isAdmin && !userAllowedPipelines.includes(p)) return false;
    if (ownerIdsToMatch) {
      // Deal must be owned by rep -- we check the deal's own ownerId if set,
      // otherwise fall back to the parent company's ownerId.
      const dealOwner = d.ownerId || d.coOwnerId;
      if (!ownerIdsToMatch.includes(dealOwner)) return false;
    }
    return true;
  });

  // Filter to deals in the active pipeline. Deals without a pipeline field
  // (legacy data, before this update deployed) get re-classified by stage ID
  // via the HS classifier instead of defaulting to mattress — that prevents
  // deals from HubSpot's other pipelines (default Sales etc.) from polluting
  // the Mattress tab.
  const pipelineDeals = reachableDeals.filter(d => {
    const p = d.pipeline || HS.pipelineForStage(d.stageId || "");
    return p === activePipeline;
  });

  // Bucket deals by stage within the current pipeline
  const byStage = currentStages.reduce((a, s) => ({...a, [s]: pipelineDeals.filter(d => d.stage === s)}), {});
  const won  = pipelineDeals.filter(d => d.stage === "Closed Won").reduce((s, d) => s + (d.amount || 0), 0);
  const pipe = pipelineDeals.filter(d => !["Closed Won","Closed Lost"].includes(d.stage)).reduce((s, d) => s + (d.amount || 0), 0);

  const handleSaveDeal = (payload) => {
    const updated = companies.map(co => {
      if (co.id !== payload.coId) {
        return {...co, deals: (co.deals || []).filter(d => d.id !== payload.id)};
      }
      const existing = (co.deals || []).find(d => d.id === payload.id);
      const newDeals = existing
        ? (co.deals || []).map(d => d.id === payload.id ? payload : d)
        : [...(co.deals || []), payload];
      return {...co, deals: newDeals};
    });
    onUpdateCompanies(updated);
    setSavingMsg("Saved to HubSpot ✓");
    setTimeout(()=>setSavingMsg(""), 2500);
    setEditDeal(null);
    setShowNew(false);
  };

  const handleDeleteDeal = (dealId) => {
    const updated = companies.map(co => ({...co, deals: (co.deals || []).filter(d => d.id !== dealId)}));
    onUpdateCompanies(updated);
    setEditDeal(null);
  };

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      {/* Pipeline tab toggle -- only shows pipelines the rep is allowed to see */}
      <div style={{display:"flex",gap:0,marginBottom:18,background:"#fff",borderRadius:12,padding:4,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",width:"fit-content",maxWidth:"100%",overflowX:"auto"}}>
        {visiblePipelines.map(p => {
          const isActive = activePipeline === p.id;
          // Count uses reachableDeals (already ownership-filtered) so reps
          // without seeAll see their own deal counts, not the total.
          const count = reachableDeals.filter(d => {
            const pipe = d.pipeline || HS.pipelineForStage(d.stageId || "");
            return pipe === p.id;
          }).length;
          return (
            <button key={p.id} onClick={()=>setActivePipelinePersist(p.id)}
              style={{
                padding:"9px 18px",borderRadius:8,border:"none",
                background:isActive ? "var(--brown)" : "transparent",
                color:isActive ? "#fff" : "var(--muted)",
                fontFamily:"Barlow,sans-serif",fontSize:13,fontWeight:600,cursor:"pointer",
                transition:"all .15s",whiteSpace:"nowrap",
                display:"flex",alignItems:"center",gap:6,
              }}>
              {p.label}
              <span style={{
                fontSize:11,padding:"1px 8px",borderRadius:99,
                background:isActive ? "rgba(255,255,255,.25)" : "var(--warm1)",
                color:isActive ? "#fff" : "var(--brown)",fontWeight:600,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",gap:14,marginBottom:24,flexWrap:"wrap",alignItems:"center"}}>
        {[
          ["Pipeline",    "$" + pipe.toLocaleString(), "#444444"],
          ["Won",         "$" + won.toLocaleString(),  "#1a8a4a"],
          ["Total Deals", pipelineDeals.length,         "#111111"],
        ].map(([l, v, c]) => (
          <div key={l} style={{flex:1,minWidth:130,background:"#fff",borderRadius:16,padding:"16px 20px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:700,fontFamily:"Barlow Condensed,sans-serif",color:c}}>{v}</div>
            <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>{l}</div>
          </div>
        ))}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
          {savingMsg && <span style={{fontSize:13,color:"var(--green)",fontWeight:600,padding:"6px 14px",borderRadius:99,background:"#e6f4ed"}}>{savingMsg}</span>}
          <Btn onClick={()=>setShowNew(true)} style={{whiteSpace:"nowrap"}}>+ New Deal</Btn>
        </div>
      </div>

      <div className="pipe-wrap">
        <div className="pipe-scroll" style={{display:"flex",gap:14,overflowX:"auto",paddingBottom:8}}>
        {currentStages.map(stage => (
          <div key={stage} style={{minWidth:220,flex:"0 0 220px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,padding:"0 2px"}}>
              <div style={{width:10,height:10,borderRadius:99,background:STAGE_COLORS[stage],flexShrink:0}}/>
              <span style={{fontSize:13,fontWeight:600,flex:1}}>{stage}</span>
              <span style={{fontSize:11,color:"var(--muted)",background:"var(--warm1)",padding:"2px 8px",borderRadius:99}}>{byStage[stage].length}</span>
              <button onClick={()=>setShowNew(true)} title="Add deal in this stage"
                style={{width:20,height:20,borderRadius:99,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:14,lineHeight:1,cursor:"pointer",color:"var(--muted)",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}
                onMouseEnter={e=>{e.currentTarget.style.background="var(--warm1)";e.currentTarget.style.color="var(--brown)";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color="var(--muted)";}}>+</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {byStage[stage].map(d => (
                <div key={d.id}
                  style={{background:"#fff",borderRadius:14,padding:"14px 16px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",cursor:"pointer",transition:"box-shadow .15s,transform .15s"}}
                  onClick={()=>setEditDeal(d)}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow="var(--sh-lg)";e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow="var(--sh)";e.currentTarget.style.transform="none";}}>
                  <div style={{fontWeight:600,fontSize:13,marginBottom:4,color:"var(--dark)"}}>{d.name}</div>
                  <div style={{fontSize:11,color:"var(--muted)",marginBottom:10}}>
                    <LinkBtn onClick={()=>{const co=companies.find(c=>c.id===d.coId); if(co && onOpenCompany) onOpenCompany(co);}}>
                      {d.coName}
                    </LinkBtn>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontWeight:700,color:"var(--brown)",fontFamily:"Barlow Condensed,sans-serif",fontSize:16}}>${(d.amount || 0).toLocaleString()}</div>
                    {(() => { const t = fmtCloseDate(d.closeDate); return t && <span style={{fontSize:10,color:"var(--muted)",background:"var(--warm1)",padding:"2px 7px",borderRadius:99}}>📅 {t}</span>; })()}
                  </div>
                  {d.notes && <div style={{marginTop:8,fontSize:11,color:"var(--muted)",fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.notes}</div>}
                  <div style={{marginTop:10,fontSize:11,color:"var(--brown)",fontWeight:600,opacity:.7}}>Click to edit →</div>
                </div>
              ))}
              {byStage[stage].length === 0 && (
                <div onClick={()=>setShowNew(true)}
                  style={{padding:"20px 0",textAlign:"center",fontSize:12,color:"var(--warm3)",fontStyle:"italic",border:"2px dashed var(--warm2)",borderRadius:12,cursor:"pointer"}}>
                  + Add deal
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>

      {showNew && <DealModal deal={null} companies={companies} defaultPipeline={activePipeline} onSave={handleSaveDeal} onDelete={handleDeleteDeal} onClose={()=>setShowNew(false)}/>}
      {editDeal && <DealModal deal={editDeal} companies={companies} onSave={handleSaveDeal} onDelete={handleDeleteDeal} onClose={()=>setEditDeal(null)}/>}
    </div>
  );
}
